import React from 'react';
import { CheckCircle, Users, CreditCard, ShieldCheck, Award, LineChart, PieChart, Briefcase, BatteryCharging, MessagesSquare } from 'lucide-react';
import { SectionProps, WithRefProps } from '../types';

interface EmployeeEmployerAdvantagesProps extends SectionProps, WithRefProps {
  handleCTAClick?: (e: React.MouseEvent) => void;
}

const EmployeeEmployerAdvantages: React.FC<EmployeeEmployerAdvantagesProps> = ({ inView, forwardedRef, handleCTAClick }) => {
  const employeeAdvantages = [
    {
      icon: <ShieldCheck className="w-6 h-6 text-primary-500" />,
      title: "Umfassender Gesundheitsschutz",
      description: "Zugang zu erweiterten Gesundheitsleistungen ohne lange Wartezeiten"
    },
    {
      icon: <CreditCard className="w-6 h-6 text-primary-500" />,
      title: "Finanzielle Entlastung",
      description: "Reduzierte Eigenkosten bei Arztbesuchen und Behandlungen"
    },
    {
      icon: <BatteryCharging className="w-6 h-6 text-primary-500" />,
      title: "Steigerung der Lebensqualität",
      description: "Präventionsmaßnahmen für mehr Energie und Wohlbefinden im Alltag"
    },
    {
      icon: <MessagesSquare className="w-6 h-6 text-primary-500" />,
      title: "Persönliche Wertschätzung",
      description: "Spürbare Anerkennung durch individuell abgestimmte Benefits"
    }
  ];

  const companyAdvantages = [
    {
      icon: <LineChart className="w-6 h-6 text-primary-500" />,
      title: "Senkung der Fehlzeiten",
      description: "Nachweisliche Reduzierung krankheitsbedingter Ausfälle um bis zu 30%"
    },
    {
      icon: <Users className="w-6 h-6 text-primary-500" />,
      title: "Höhere Mitarbeiterbindung",
      description: "Durchschnittlich 40% längere Unternehmenszugehörigkeit"
    },
    {
      icon: <Award className="w-6 h-6 text-primary-500" />,
      title: "Stärkung der Arbeitgebermarke",
      description: "Bessere Position im Wettbewerb um Fachkräfte"
    },
    {
      icon: <PieChart className="w-6 h-6 text-primary-500" />,
      title: "Steuerliche Optimierung",
      description: "Effiziente Nutzung staatlicher Fördermittel und Steuervergünstigungen"
    }
  ];

  return (
    <section id="employee-employer" className="section-padding bg-gradient-to-br from-gray-50 to-secondary-50">
      <div ref={forwardedRef} className={`container mx-auto px-6 fade-up ${inView ? 'in-view' : ''}`}>
        <div className="text-center mb-16">
          <div className="inline-block mb-4 text-sm font-semibold text-primary-500 px-3 py-1 bg-white rounded-full shadow-sm">
            STRATEGISCH & WERTORIENTIERT
          </div>
          <h2 className="mb-6 text-3xl md:text-4xl font-bold">
            Gesunde Vorteile für Mitarbeitende & Unternehmen
          </h2>
          <p className="max-w-3xl mx-auto text-lg text-gray-700">
            Ein durchdachtes Benefits-Werk schafft messbare Vorteile für alle Beteiligten 
            und steigert Mitarbeiterzufriedenheit und Unternehmenserfolg gleichermaßen.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-12">
          {/* Employee Benefits Column */}
          <div className="bg-white p-8 rounded-xl shadow-lg">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-primary-100 p-2 rounded-full">
                <Briefcase className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">VORTEILE FÜR MITARBEITENDE</h3>
            </div>
            
            <div className="space-y-6">
              {employeeAdvantages.map((advantage, index) => (
                <div key={index} className="flex items-start gap-4 p-4 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="mt-1 flex-shrink-0">
                    {advantage.icon}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{advantage.title}</h4>
                    <p className="text-gray-600 mt-1">{advantage.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Company Benefits Column */}
          <div className="bg-white p-8 rounded-xl shadow-lg">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-primary-100 p-2 rounded-full">
                <Award className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">VORTEILE FÜR UNTERNEHMEN</h3>
            </div>
            
            <div className="space-y-6">
              {companyAdvantages.map((advantage, index) => (
                <div key={index} className="flex items-start gap-4 p-4 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="mt-1 flex-shrink-0">
                    {advantage.icon}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{advantage.title}</h4>
                    <p className="text-gray-600 mt-1">{advantage.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <div className="mt-12 text-center">
          <div className="bg-white p-6 rounded-lg shadow-md inline-block max-w-2xl">
            <p className="text-gray-700 mb-6">
              <span className="font-semibold">Unsere Kunden berichten:</span> Durchschnittlich 65% weniger Fluktuation 
              und 30% reduzierte Krankheitstage nach Implementierung eines maßgeschneiderten Benefits-Werk.
            </p>
            
            {handleCTAClick && (
              <button 
                onClick={handleCTAClick}
                className="btn btn-primary px-6 py-3 rounded-lg font-semibold shadow-md">
                Kostenlose Potentialanalyse vereinbaren
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default EmployeeEmployerAdvantages;